import dayjs from 'dayjs'
import { Layout, PlotlyHTMLElement } from 'features/plotly'
import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useState,
} from 'react'

interface ExtendedPlotlyHTMLElement extends PlotlyHTMLElement {
  _fullLayout: Layout & {
    xaxis: {
      p2d: (x: number) => string
      _offset: number
    }
  }
}

function isExtendedPlotlyHTMLElement(
  element: PlotlyHTMLElement
): element is ExtendedPlotlyHTMLElement {
  // Disabling eslint because this is a type guard and we need to hack around plotly.js's type definitions
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return !!(element as any)?._fullLayout?.xaxis
}

export interface CursorDataPoint {
  sensorId: string
  yValue: number | null
  xValue: number | string | null
  unit?: string
}

interface CursorDataContextType {
  cursorData: Record<string, CursorDataPoint>
  setCursorData: React.Dispatch<
    React.SetStateAction<Record<string, CursorDataPoint>>
  >
  updateCursorData: (
    sensorId: string,
    yValue: number | null,
    xValue: number | null,
    unit?: string
  ) => void
  clearCursorData: () => void
  getXValueAtCursor: (
    e: React.MouseEvent,
    plotElement: PlotlyHTMLElement
  ) => number | null
}

const CursorDataContext = createContext<CursorDataContextType | undefined>(
  undefined
)

interface CursorDataProviderProps {
  children: ReactNode
}

export const CursorDataProvider: React.FC<CursorDataProviderProps> = ({
  children,
}) => {
  const [cursorData, setCursorData] = useState<Record<string, CursorDataPoint>>(
    {}
  )

  const updateCursorData = (
    sensorId: string,
    yValue: number | null,
    xValue: number | null,
    unit?: string
  ) => {
    setCursorData((prev) => ({
      ...prev,
      [sensorId]: { sensorId, yValue, xValue, unit },
    }))
  }

  const clearCursorData = () => {
    setCursorData({})
  }

  const getXValueAtCursor = useCallback(
    (e: React.MouseEvent, plotElement: PlotlyHTMLElement): number | null => {
      if (!isExtendedPlotlyHTMLElement(plotElement)) return null

      const layout = plotElement._fullLayout
      const xaxis = layout.xaxis
      const plotRect = plotElement.getBoundingClientRect() // Includes the offset of the xaxis so we need to subtract it
      const relativeX = e.clientX - plotRect.left - xaxis._offset
      const xDateString = xaxis.p2d(relativeX)
      const xTimestamp = dayjs(xDateString).valueOf()
      return xTimestamp
    },
    []
  )

  return (
    <CursorDataContext.Provider
      value={{
        cursorData,
        setCursorData,
        updateCursorData,
        clearCursorData,
        getXValueAtCursor,
      }}>
      {children}
    </CursorDataContext.Provider>
  )
}

export const useCursorData = (): CursorDataContextType => {
  const context = useContext(CursorDataContext)
  if (context === undefined) {
    throw new Error('useCursorData must be used within a CursorDataProvider')
  }
  return context
}
